import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SocialRegister from './pages/SocialRegister';
import Dashboard from './pages/Dashboard';
import AuthCallback from './pages/AuthCallback';
import AdminFirms from './pages/admin/Firms';
import MasterListPage from './pages/admin/MasterListPage';
import RolePermissionPage from './pages/admin/RolePermissionPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ServiceTypeList from './pages/ServiceTypeList';
import AttachmentList from './pages/AttachmentList';
import { debugEnvironment, validateEnvironment } from './utils/env-debug';
import config from './config';
import RolesManager from './components/admin/RolesManager';
import PermissionsManager from './components/admin/PermissionsManager';
import AdminUsers from './pages/admin/Users';
import FinancialReports from './pages/admin/FinancialReports';
import OfficeLocationList from './pages/OfficeLocationList';
import ExpertList from './pages/ExpertList';
import IntakeFormTypeList from './pages/IntakeFormTypeList';
import DynamicIntakeForm from './components/intake/DynamicIntakeForm';
import Cases from './pages/admin/Cases';
import CaseSummary from './pages/CaseSummary';
import MeetingSelection from './pages/MeetingSelection';
import SlotSelection from './pages/SlotSelection';
import FirmAdminSlotSelection from './pages/FirmAdminSlotSelection';
import CasesWithSlots from './pages/admin/CasesWithSlots';
import IntakeForm from './pages/IntakeForm';
import MedicalRecordsUpload from './pages/MedicalRecordsUpload';
import EmailTemplates from './pages/admin/EmailTemplates';
import EmailLogs from './pages/admin/EmailLogs';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/ProtectedRoute';

// Import our styles
import './assets/scss/main.scss';

// Placeholder components for other routes
const Documents = () => <div>Documents Page</div>;
const Signatures = () => <div>Signatures Page</div>;
import Settings from './pages/Settings';

// Admin placeholder components
const AdminDashboard = () => <div>Admin Dashboard</div>;

function App() {
  const { loading, isAuthenticated, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const isFirmAdmin = hasRole('firm_admin');
  const isExpertUser = hasRole('expert_user');
  const canAccessAdmin = isAdmin || isFirmAdmin || isExpertUser;

  // Environment validation on app start
  useEffect(() => {
    if (config.IS_DEVELOPMENT) {
      debugEnvironment();
      validateEnvironment();
    }
  }, []);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '100vh' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer position='top-right' />
      <Routes>
        {/* Auth Routes */}
        <Route
          path='/'
          element={
            !isAuthenticated ? <AuthLayout /> : <Navigate to='/dashboard' />
          }
        >
          <Route index element={<Login />} />
          <Route path='login' element={<Login />} />
          <Route path='register' element={<Register />} />
          <Route path='forgot-password' element={<ForgotPassword />} />
          <Route path='reset-password' element={<ResetPassword />} />
        </Route>

        {/* Social Register Route */}
        <Route path='/auth/social-register' element={<AuthLayout />}>
          <Route index element={<SocialRegister />} />
        </Route>

        {/* Auth Callback Route */}
        <Route path='/auth/callback' element={<AuthCallback />} />

        {/* Protected Routes */}
        <Route
          path='/dashboard'
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path='cases' element={<Cases />} />
          <Route path='documents' element={<Documents />} />
          <Route path='signatures' element={<Signatures />} />
          <Route path='profile' element={<Profile />} />
          <Route path='notifications' element={<Notifications />} />
          <Route path='settings' element={<Settings />} />
        </Route>

        {/* Admin Routes */}
        <Route
          path='/admin'
          element={
            isAuthenticated ? (
              canAccessAdmin ? (
                <MainLayout />
              ) : (
                <Navigate to='/dashboard' replace />
              )
            ) : (
              <ProtectedRoute isAuthenticated={false}>
                <MainLayout />
              </ProtectedRoute>
            )
          }
        >
          <Route index element={<AdminDashboard />} />
          {isAdmin && <Route path='firms' element={<AdminFirms />} />}
          {isAdmin && <Route path='masters/:type' element={<MasterListPage />} />}
          {isAdmin && <Route path='users' element={<AdminUsers />} />}
          {isAdmin && <Route path='roles' element={<RolesManager />} />}
          {isAdmin && (
            <Route path='permissions' element={<PermissionsManager />} />
          )}
          {isAdmin && (
            <Route path='financial-reports' element={<FinancialReports />} />
          )}
          <Route path='service-types' element={<ServiceTypeList />} />
          <Route path='intake-form-types' element={<IntakeFormTypeList />} />
          <Route path='dynamic-intake-form' element={<DynamicIntakeForm />} />
          <Route path='attachments' element={<AttachmentList />} />
          {isAdmin && (
            <Route path='roles-permissions' element={<RolePermissionPage />} />
          )}
          <Route path='office-locations' element={<OfficeLocationList />} />
          <Route path='experts' element={<ExpertList />} />
          <Route path='cases' element={<Cases />} />
          <Route path='cases/:id' element={<CaseSummary />} />
          <Route path='cases/:id/slots' element={<SlotSelection />} />
          <Route path='slot-selection/:id' element={<SlotSelection />} />
          <Route path='cases-with-slots' element={<CasesWithSlots />} />
          {isAdmin && <Route path='email-templates' element={<EmailTemplates />} />}
          {isAdmin && <Route path='email-logs' element={<EmailLogs />} />}
          {isAdmin && <Route path='settings' element={<Settings />} />}
        </Route>

        {/* Meeting Selection Route (Public) */}
        <Route path='/meeting-selection/:id' element={<MeetingSelection />} />

        {/* Firm Admin Slot Selection Route (Public - accessed via email link) */}
        <Route path='/firm-slot-selection/:id' element={<FirmAdminSlotSelection />} />

        {/* Intake Form Route (Public) */}
        <Route path='/intake-form/:token' element={<IntakeForm />} />

        {/* Medical Records Upload Route (Public) */}
        <Route path='/medical-records/upload/:caseId' element={<MedicalRecordsUpload />} />

        {/* Legacy settings URL */}
        <Route path='/settings' element={<Navigate to='/admin/settings' replace />} />

        {/* Catch all route */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </Router>
  );
}

export default App;
